import { Component, computed, inject, input, output } from '@angular/core';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { toSignal } from '@angular/core/rxjs-interop';

export interface Step {
  label: string;
  mobileLabel?: string; // Optional shorter label for mobile
  number: number;
}

@Component({
  selector: 'app-stepper',
  templateUrl: './stepper.component.html',
  styleUrls: ['./stepper.component.scss'],
  standalone: false
})
export class StepperComponent {
  private breakpointObserver = inject(BreakpointObserver);

  /**
   * Array of steps to display in the stepper
   */
  steps = input<Step[]>([]);

  /**
   * The index of the currently active step (0-based)
   */
  activeStepIndex = input<number>(0);

  /**
   * Emits when a step is clicked
   */
  stepClick = output<number>();

  /**
   * Detect if we're on mobile (≤768px)
   */
  isMobile = toSignal(
    this.breakpointObserver.observe([Breakpoints.XSmall, Breakpoints.Small])
  );

  /**
   * Get the appropriate label for a step based on screen size
   */
  getStepLabel(step: Step): string {
    if (this.isMobile()?.matches && step.mobileLabel) {
      return step.mobileLabel;
    }
    return step.label;
  }

  /**
   * Check if a step is active
   */
  isActive(index: number): boolean {
    return index === this.activeStepIndex();
  }

  /**
   * Check if a step is completed (before the active step)
   */
  isCompleted(index: number): boolean {
    return index < this.activeStepIndex();
  }

  /**
   * Handle step click
   */
  onStepClick(index: number): void {
    this.stepClick.emit(index);
  }

  /**
   * Get the progress percentage for the progress line
   */
  getProgressPercentage(): number {
    if (this.steps().length <= 1) return 0;
    return (this.activeStepIndex() / (this.steps().length - 1)) * 100;
  }
}
